import React, { createContext, useContext, useReducer, useEffect } from 'react';

// 사용자 프로필 타입 정의
export interface UserProfile {
  // 기본 정보
  name: string;
  email: string;
  company: string;
  position: string;
  industry: string;
  
  // 비즈니스 정보
  businessType: string;
  targetMarket: string;
  mainProduct: string;
  businessStage: string;
  
  // 기술/관심사
  skills: string[];
  interests: string[];
  experience: string;
  
  // 사업 관련
  budget: string;
  timeline: string;
  goals: string[];
  
  // 개인화 설정
  preferredTone: 'formal' | 'casual' | 'professional';
  language: 'ko' | 'en';
}

// 초기 상태
const initialProfile: UserProfile = {
  name: '',
  email: '',
  company: '',
  position: '',
  industry: '',
  businessType: '',
  targetMarket: '',
  mainProduct: '',
  businessStage: '',
  skills: [],
  interests: [],
  experience: '',
  budget: '',
  timeline: '',
  goals: [],
  preferredTone: 'professional',
  language: 'ko',
};

// 액션 타입
type ProfileAction = 
  | { type: 'UPDATE_PROFILE'; payload: Partial<UserProfile> }
  | { type: 'RESET_PROFILE' }
  | { type: 'LOAD_PROFILE'; payload: UserProfile }
  | { type: 'ADD_SKILL'; payload: string }
  | { type: 'REMOVE_SKILL'; payload: string }
  | { type: 'ADD_INTEREST'; payload: string }
  | { type: 'REMOVE_INTEREST'; payload: string }
  | { type: 'ADD_GOAL'; payload: string }
  | { type: 'REMOVE_GOAL'; payload: string };

// 리듀서
const profileReducer = (state: UserProfile, action: ProfileAction): UserProfile => {
  switch (action.type) {
    case 'UPDATE_PROFILE':
      return { ...state, ...action.payload };
    
    case 'RESET_PROFILE':
      return initialProfile;
    
    case 'LOAD_PROFILE':
      return action.payload;
    
    case 'ADD_SKILL':
      return {
        ...state,
        skills: [...state.skills, action.payload].filter((skill, index, arr) => 
          arr.indexOf(skill) === index
        )
      };
    
    case 'REMOVE_SKILL':
      return {
        ...state,
        skills: state.skills.filter(skill => skill !== action.payload)
      };
    
    case 'ADD_INTEREST':
      return {
        ...state,
        interests: [...state.interests, action.payload].filter((interest, index, arr) => 
          arr.indexOf(interest) === index
        )
      };
    
    case 'REMOVE_INTEREST':
      return {
        ...state,
        interests: state.interests.filter(interest => interest !== action.payload)
      };
    
    case 'ADD_GOAL':
      return {
        ...state,
        goals: [...state.goals, action.payload].filter((goal, index, arr) => 
          arr.indexOf(goal) === index
        )
      };
    
    case 'REMOVE_GOAL':
      return {
        ...state,
        goals: state.goals.filter(goal => goal !== action.payload)
      };
    
    default:
      return state;
  }
};

// 컨텍스트 타입
interface UserProfileContextType {
  profile: UserProfile;
  updateProfile: (updates: Partial<UserProfile>) => void;
  resetProfile: () => void;
  saveProfile: () => void;
  loadProfile: () => void;
  addSkill: (skill: string) => void;
  removeSkill: (skill: string) => void;
  addInterest: (interest: string) => void;
  removeInterest: (interest: string) => void;
  addGoal: (goal: string) => void;
  removeGoal: (goal: string) => void;
  isProfileComplete: () => boolean;
}

// 컨텍스트 생성
const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

// 로컬 스토리지 키
const PROFILE_STORAGE_KEY = 'easygpt_user_profile';

// 프로바이더 컴포넌트
export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, dispatch] = useReducer(profileReducer, initialProfile);

  // 프로필 업데이트
  const updateProfile = (updates: Partial<UserProfile>) => {
    dispatch({ type: 'UPDATE_PROFILE', payload: updates });
  };

  // 프로필 초기화
  const resetProfile = () => {
    dispatch({ type: 'RESET_PROFILE' });
    localStorage.removeItem(PROFILE_STORAGE_KEY);
  };

  // 프로필 저장
  const saveProfile = () => {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
  };

  // 프로필 불러오기
  const loadProfile = () => {
    const saved = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (saved) {
      try {
        const parsedProfile = JSON.parse(saved);
        dispatch({ type: 'LOAD_PROFILE', payload: parsedProfile });
      } catch (error) {
        console.error('프로필 불러오기 실패:', error);
      }
    }
  };

  // 스킬 관리
  const addSkill = (skill: string) => {
    if (skill.trim()) {
      dispatch({ type: 'ADD_SKILL', payload: skill.trim() });
    }
  };

  const removeSkill = (skill: string) => {
    dispatch({ type: 'REMOVE_SKILL', payload: skill });
  };

  // 관심사 관리
  const addInterest = (interest: string) => {
    if (interest.trim()) {
      dispatch({ type: 'ADD_INTEREST', payload: interest.trim() });
    }
  };

  const removeInterest = (interest: string) => {
    dispatch({ type: 'REMOVE_INTEREST', payload: interest });
  };

  // 목표 관리
  const addGoal = (goal: string) => {
    if (goal.trim()) {
      dispatch({ type: 'ADD_GOAL', payload: goal.trim() });
    }
  };

  const removeGoal = (goal: string) => {
    dispatch({ type: 'REMOVE_GOAL', payload: goal });
  };

  // 프로필 완성도 체크
  const isProfileComplete = () => {
    return !!(
      profile.name &&
      profile.company &&
      profile.position &&
      profile.industry &&
      profile.businessType &&
      profile.targetMarket
    );
  };

  // 초기 로드
  useEffect(() => {
    loadProfile();
  }, []);

  // 프로필 변경 시 자동 저장
  useEffect(() => {
    if (profile.name) { // 기본 정보가 있을 때만 저장
      saveProfile();
    }
  }, [profile]);

  return (
    <UserProfileContext.Provider value={{
      profile,
      updateProfile,
      resetProfile,
      saveProfile,
      loadProfile,
      addSkill,
      removeSkill,
      addInterest,
      removeInterest,
      addGoal,
      removeGoal,
      isProfileComplete,
    }}>
      {children}
    </UserProfileContext.Provider>
  );
};

// 훅
export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};